import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Admin } from '@scriptpad/shared';
import { fetchMe, login as apiLogin, logout as apiLogout, refreshSession, setAccessToken, setOnUnauthorised } from '../api/client.js';

interface AuthCtx {
  admin: Admin | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  const reset = useCallback(() => {
    setAccessToken(null);
    setAdmin(null);
  }, []);

  // React StrictMode double-invokes effects in dev. The shared in-flight
  // promise in api/client.ts already coalesces refresh calls, but we also
  // guard here so we don't run two parallel `fetchMe` chains and end up
  // briefly setting loading=false without an admin (which would race the
  // ProtectedLayout into a flash redirect to /login).
  const bootstrapped = useRef(false);

  useEffect(() => {
    setOnUnauthorised(reset);
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    void (async () => {
      let session: Awaited<ReturnType<typeof refreshSession>> = null;
      try {
        session = await refreshSession();
      } catch {
        // Transient refresh failure on initial load (server down, network,
        // CORS misconfig, etc.). We can't tell whether the user has a
        // session, so we drop them on /login as the safe fallback rather
        // than leaving the spinner up indefinitely. In-session refreshes
        // (the 401-retry interceptor) handle this case differently and
        // leave the user logged in.
        session = null;
      }
      if (session) {
        try {
          setAdmin(await fetchMe());
        } catch {
          reset();
        }
      }
      setLoading(false);
    })();
  }, [reset]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiLogin(email, password);
    setAdmin(res.admin);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    reset();
  }, [reset]);

  const value = useMemo(() => ({ admin, loading, login, logout }), [admin, loading, login, logout]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAuth = (): AuthCtx => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
